import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { PlatformLayoutComponent } from './shared/components/platform-layout/platform-layout.component';
import { AuthGuardService } from '@app/core/services/auth-guard.service';
import { LandingComponent } from '@app/core/components/landing/landing.component';
import { LandingRoutes } from '@app/core/components/landing/landing-routes';
import { LandingGuardService } from '@app/core/services/landing-guard.service';
import { AuthComponent } from '@app/core/components/auth/auth.component';
import { AuthRoutes } from '@app/core/components/auth/auth-routes';
import { ImpersonateGuardService } from '@app/core/services/impersonate-guard.service';
import { DashboardGuardService } from './core/services/dashboard-guard.service';
import { KpaThemeSampleComponent } from './shared/kendo/sample/sample.component';
import { KpaThemeSampleGuard } from './shared/kendo/sample/sample.guard';
import { KpaComponentLibraryGuardService } from './core/services/kpa-component-library-guard.service';
import { PrivacyRequestPortalCustomerFacingAuthGuardService } from './privacy-request-portal-customer-facing/shared/services/privacy-request-portal-customer-facing-auth-guard.service';

const routes: Routes = [
	{ path: AuthRoutes.AuthenticateCallback, component: AuthComponent },
	{ path: AuthRoutes.Logout, component: AuthComponent },
	{ path: AuthRoutes.LogoutCallback, component: AuthComponent },
	{ path: AuthRoutes.ImpersonateCallback, component: AuthComponent, canActivate: [ImpersonateGuardService] },
	{ path: AuthRoutes.StartImpersonation + '/:userId', component: AuthComponent, canActivate: [ImpersonateGuardService] },
	{ path: AuthRoutes.EndImpersonation, canActivate: [AuthGuardService], component: AuthComponent },
	{ path: AuthRoutes.EndImpersonationCommit, component: AuthComponent },
	{ path: AuthRoutes.EndImpersonationHRM, component: AuthComponent },
	{ path: AuthRoutes.ClearSessionHRM, component: AuthComponent },
	{ path: AuthRoutes.LogoutCommit, component: AuthComponent },
	{ path: AuthRoutes.ClearSessionExternalHRM, component: AuthComponent },
	{ path: AuthRoutes.LogoutExternalCommit, component: AuthComponent },
	{ path: AuthRoutes.LoginBootstrapStart, component: AuthComponent },
	{ path: AuthRoutes.LoginBootstrapHrm, component: AuthComponent },
	{ path: AuthRoutes.LoginBootstrapCommit, component: AuthComponent },
	{ path: AuthRoutes.Blocked, loadChildren: () => import('./core/components/blocked/blocked.module').then((m) => m.BlockedModule) },
	{
		path: AuthRoutes.SsoLocationSelection,
		loadChildren: () =>
			import('./core/components/sso-location-selection/sso-location-selection.module').then((m) => m.SsoLocationSelectionModule),
	},
	{ path: 'marketing', loadChildren: () => import('../app/marketing/marketing.module').then((m) => m.MarketingModule) },
	{
		// authenticated routes
		path: '',
		canActivate: [AuthGuardService],
		component: PlatformLayoutComponent,
		runGuardsAndResolvers: 'always',

		children: [
			{ path: '', pathMatch: 'full', redirectTo: '/landing' },
			{
				path: LandingRoutes.RedirectRoute,
				canActivate: [LandingGuardService],
				component: LandingComponent,
			},
			{
				path: LandingRoutes.DefaultRoute,
				canActivate: [DashboardGuardService],
				loadChildren: () => import('../app/dashboard/dashboard.module').then((m) => m.DashboardModule),
			},
			{ path: 'dashboard', loadChildren: () => import('../app/dashboard/dashboard.module').then((m) => m.DashboardModule) },
			{ path: 'kpi', loadChildren: () => import('../app/glba/glba.module').then((m) => m.GLBAModule) },
			{ path: 'vehicle', loadChildren: () => import('../app/vehicle/vehicle.module').then((m) => m.VehicleModule) },
			{
				path: 'environment',
				loadChildren: () =>
					import('./environment/environment-regulatory-data/environment-regulatory-data.module').then(
						(m) => m.EnvironmentRegulatoryDataModule
					),
			},
			{
				path: 'environment',
				loadChildren: () =>
					import('./environment/environment-regulatory-data/environment-regulatory-data.module').then(
						(m) => m.EnvironmentRegulatoryDataModule
					),
			},
			{
				path: 'feedbackmanagement',
				loadChildren: () => import('../app/feedback-management/feedback-management.module').then((m) => m.FeedbackManagementModule),
			},
			{ path: 'northstar', loadChildren: () => import('../app/northstar/northstar.module').then((m) => m.NorthstarModule) },
			{ path: 'employee', loadChildren: () => import('../app/employee/employee.module').then((m) => m.EmployeeModule) },
			{ path: 'clients', loadChildren: () => import('../app/clients/clients.module').then((m) => m.ClientsModule) },
			{ path: 'home', loadChildren: () => import('../app/home/home.module').then((m) => m.HomeModule) },
			{ path: 'reporting', loadChildren: () => import('../app/reporting/reporting.module').then((m) => m.ReportingModule) },
			{ path: 'inspections', loadChildren: () => import('../app/inspections/inspections.module').then((m) => m.InspectionsModule) },
			{ path: 'incidents', loadChildren: () => import('../app/incidents/incidents.module').then((m) => m.IncidentsModule) },
			{ path: 'import', loadChildren: () => import('../app/import/import.module').then((m) => m.ImportModule) },
			{ path: 'roles', loadChildren: () => import('../app/roles-permissions/roles.module').then((m) => m.RolesModule) },
			{ path: 'notifications', loadChildren: () => import('../app/notifications/notifications.module').then((m) => m.NotificationsModule) },
			{ path: 'training', redirectTo: '/dashboard' },
			{ path: 'tasks', loadChildren: () => import('../app/tasks/tasks.module').then((m) => m.TasksModule) },
			{ path: 'licensespermits', loadChildren: () => import('../app/veratrack/veratrack.module').then((m) => m.VeraTrackModule) },
			{
				path: 'vendormanagement',

				loadChildren: () => import('../app/vendor-management/vendor-management.module').then((m) => m.VendorManagementModule),
			},
			{
				path: 'website-risk-assessment',
				loadChildren: () =>
					import('../app/website-risk-assessment/website-risk-assessment.module').then((m) => m.WebsiteRiskAssessmentModule),
			},
			{
				path: 'requestPortal',
				loadChildren: () => import('../app/privacy-request-portal/privacy-request-portal.module').then((m) => m.PrivacyRequestPortalModule),
			},
			{
				path: 'resource-library',
				loadChildren: () => import('../app/resource-library/resource-library.module').then((m) => m.ResourceLibraryModule),
			},
			{
				path: 'documents',
				loadChildren: () => import('../app/documents/documents.module').then((m) => m.DocumentsModule),
			},
			{
				path: 'mko',
				loadChildren: () => import('../app/mko-migration/mko-migration.module').then((m) => m.MkoMigrationModule),
			},
			{ path: 'lead-response-audit', loadChildren: () => import('../app/chat-audit/chat-audit.module').then((m) => m.ChatAuditModule) },
		],
	},
	{
		path: LandingRoutes.MobileRoute,
		loadChildren: () => import('../app/mobile-landing/mobile-landing.module').then((m) => m.MobileLandingModule),
	},
	{
		path: 'registration',
		loadChildren: () =>
			import('./user-account/registration/registration-v2/registration-v2.module').then((m) => m.RegistrationV2Module),
	},
	{ path: 'useraccount', loadChildren: () => import('../app/user-account/user-account.module').then((m) => m.UserAccountModule) },
	{
		/* NOTES:
       1)  This is needed here (as opposed to being declared in the routes that use PlatformLayoutComponent) so
       we don't get the PlatformLayout on this page, too.
       2)  If the location/route of this page is changed, you MUST update MKO's /Commerce/AccountCreate.aspx page as well.
    */
		path: 'home/contactusali',
		loadChildren: () => import('./home/contact-us-ali/contact-us-ali.module').then((m) => m.ContactUsAliModule),
	},
	{ path: 'error', loadChildren: () => import('./core/components/error/error.module').then((m) => m.ErrorModule) },
	{ path: 'theme', component: KpaThemeSampleComponent, canActivate: [KpaThemeSampleGuard] },
	{
		path: 'kpa-component-library',
		canActivate: [KpaComponentLibraryGuardService],
		loadChildren: () =>
			import('../app/kpa-component-library/kpa-component-library-viewer.module').then((m) => m.KpaComponentLibraryViewerModule),
	},
	{
		path: 'request-portal-form',
		canActivate: [PrivacyRequestPortalCustomerFacingAuthGuardService],
		loadChildren: () =>
			import('../app/privacy-request-portal-customer-facing/privacy-request-portal-customer-facing.module').then(
				(m) => m.PrivacyRequestPortalCustomerFacingModule
			),
	},
];

@NgModule({
	imports: [
		RouterModule.forRoot(routes, {
			enableTracing: false,
		}),
	],
	exports: [RouterModule],
})
export class AppRoutingModule {}
